import { fetchSessionLockSettings } from '@/lib/api/settings';
import { clearAuthSession, getAuthToken, subscribeAuthStateChange } from '@/lib/api/token-store';

// Fallback when the server setting has never been fetched successfully.
const DEFAULT_IDLE_LOCK_MINUTES = 10;
// pointermove storms must not thrash setTimeout — re-arm at most once per this window.
const REARM_THROTTLE_MS = 5000;
const ACTIVITY_EVENTS = ['pointerdown', 'pointermove', 'keydown', 'wheel', 'touchstart'];

let installed = false;
// Last known server value; kept across fetch failures so a flaky network never changes the
// effective timeout mid-session.
let idleLockMinutes = DEFAULT_IDLE_LOCK_MINUTES;
let timerId = null;
let lastArmAt = 0;
// Epoch ms when the idle lock will fire (null when disarmed/disabled). Published for the
// status-bar countdown; moves in ≤5s steps while the user is active (re-arm throttle).
let lockDeadlineMs = null;
const DEADLINE_CHANGE_EVENT = 'healo-session-lock-deadline-change';

function setLockDeadline(next) {
  if (lockDeadlineMs === next) return;
  lockDeadlineMs = next;
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(DEADLINE_CHANGE_EVENT));
  }
}

/** Epoch ms of the pending idle auto-lock, or null when no lock is armed. */
export function getSessionLockDeadline() {
  return lockDeadlineMs;
}

export function subscribeSessionLockDeadline(listener) {
  if (typeof window === 'undefined' || typeof listener !== 'function') {
    return () => {};
  }
  const handler = () => listener(lockDeadlineMs);
  window.addEventListener(DEADLINE_CHANGE_EVENT, handler);
  return () => window.removeEventListener(DEADLINE_CHANGE_EVENT, handler);
}

/**
 * Lock the app: clear the active session so the auth event freezes every workspace and shows
 * the roster lock screen. Lock ≠ logout — no /api/logout call and no destroyWorkspace here:
 * the frozen tree must survive so PIN re-entry thaws the exact same workspace.
 */
export function lockSession() {
  if (!getAuthToken()) return;
  clearAuthSession();
}

function clearIdleTimer() {
  if (timerId !== null) {
    clearTimeout(timerId);
    timerId = null;
  }
  setLockDeadline(null);
}

// Single timeout, re-created on every (re)arm; reads idleLockMinutes at arm time so a PUT
// takes effect on the next activity without a reload. 0 disables the auto-lock entirely.
function armIdleTimer() {
  clearIdleTimer();
  lastArmAt = Date.now();
  if (!getAuthToken()) return;
  if (!(idleLockMinutes > 0)) return;
  timerId = setTimeout(() => {
    timerId = null;
    lockSession();
  }, idleLockMinutes * 60_000);
  setLockDeadline(lastArmAt + idleLockMinutes * 60_000);
}

function handleActivity() {
  // No counting while logged out — the lock screen itself must never re-arm anything.
  if (!getAuthToken()) return;
  if (Date.now() - lastArmAt < REARM_THROTTLE_MS) return;
  armIdleTimer();
}

async function refreshIdleLockConfig() {
  if (!getAuthToken()) return;
  try {
    const settings = await fetchSessionLockSettings();
    idleLockMinutes = settings.idleLockMinutes;
  } catch (_error) {
    // Keep the last known value (or the default) — never disable/shorten on a failed fetch.
  }
  // Recompute the pending deadline from the freshly fetched minutes; the session may have
  // ended while the fetch was in flight.
  if (getAuthToken()) {
    armIdleTimer();
  } else {
    clearIdleTimer();
  }
}

/**
 * Install the idle auto-lock: while a session is active, inactivity longer than the
 * server-configured idleLockMinutes locks via lockSession(). Idempotent — the window-level
 * activity listeners and the auth subscription are installed exactly once.
 */
export function registerIdleAutoLock() {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  for (const type of ACTIVITY_EVENTS) {
    window.addEventListener(type, handleActivity, { capture: true, passive: true });
  }

  subscribeAuthStateChange(() => {
    if (getAuthToken()) {
      // Arm immediately with the last known minutes; the fetch re-arms with fresh ones.
      armIdleTimer();
      void refreshIdleLockConfig();
    } else {
      clearIdleTimer();
    }
  });

  if (getAuthToken()) {
    armIdleTimer();
    void refreshIdleLockConfig();
  }
}

let hotkeyInstalled = false;

// Cmd/Ctrl+L locks instantly from anywhere — the keyboard twin of the profile-switcher "Lock"
// button. Deliberately fires regardless of focus (a security shortcut must work mid-typing);
// the combo produces no character and we preventDefault, and lockSession() no-ops while logged
// out so the lock screen stays inert. event.key (not code) follows the user's layout, matching
// the sidebar's Cmd/Ctrl+B; Alt-holding mutates event.key off "l", so no explicit alt guard.
function handleLockHotkey(event) {
  if (!(event.metaKey || event.ctrlKey)) return;
  if (typeof event.key !== 'string' || event.key.toLowerCase() !== 'l') return;
  event.preventDefault();
  lockSession();
}

/**
 * Install the Cmd/Ctrl+L quick-lock shortcut app-wide. Idempotent — the window keydown
 * listener is attached exactly once.
 */
export function registerLockHotkey() {
  if (hotkeyInstalled || typeof window === 'undefined') return;
  hotkeyInstalled = true;
  window.addEventListener('keydown', handleLockHotkey);
}
